import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import {
	useOrgUserCountErrorMessage,
	useSelector,
	useSetMeter,
	useSetOrgUserCount,
} from '../context/hooks';
import { selectCanAdvanceUsageStep, selectMeter, selectOrgUserCount } from '../context/selectors';

import { ChoiceCard } from './choice-card';
import { StepNav } from './step-nav';
import { Stepper } from './stepper';

import type { BulletOrIconListItem } from './strings';
import type { LogoBrandV3 } from '../../../../libraries/wpl/foundations/src/logo-v3';
import type { Meter } from '../context/types';

type MeterEntry = {
	label: string;
	description: string | ReactNode;
	icon?: LogoBrandV3;
	list?: BulletOrIconListItem[];
	info?: string | ReactNode;
};

export const UsageStep = () => {
	const intl = useIntl();
	const meter = useSelector(selectMeter);
	const setMeter = useSetMeter();
	const orgUserCount = useSelector(selectOrgUserCount);
	const setOrgUserCount = useSetOrgUserCount();
	const orgUserCountError = useOrgUserCountErrorMessage();
	const canAdvance = useSelector(selectCanAdvanceUsageStep);
	const [orgUserCountInput, setOrgUserCountInput] = useState(
		orgUserCount === undefined ? '' : String(orgUserCount),
	);

	const USAGE_STRINGS = useMemo<Record<Meter, MeterEntry>>(
		() => ({
			rovoCredits: {
				label: 'Rovo Credits',
				description: intl.formatMessage({
					id: 'ubp.calculator.usage.rovoCredits.description',
					defaultMessage:
						'Use Rovo Search to find information, Rovo chat for insights, and Rovo agents to automate workflows.',
				}),
			},
			automations: {
				label: 'Automations',
				description: intl.formatMessage({
					id: 'ubp.calculator.usage.automations.description',
					defaultMessage: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
				}),
			},
			csResolutions: {
				label: 'CS Resolutions',
				description: intl.formatMessage({
					id: 'ubp.calculator.usage.csResolutions.description',
					defaultMessage: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
				}),
			},
			assetsObjects: {
				label: 'Assets Objects',
				description: intl.formatMessage({
					id: 'ubp.calculator.usage.assetsObjects.description',
					defaultMessage: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
				}),
			},
		}),
		[intl],
	);

	const usageMeters = Object.keys(USAGE_STRINGS) as Meter[];

	return (
		<TypographyStyles>
			<Stepper />
			<h1 css={pageTitleStyle}>
				{intl.formatMessage({
					id: 'ubp.calculator.usage.pageTitle',
					defaultMessage: 'Let\u2019s figure out your usage needs',
				})}
			</h1>
			<h3 id="usage-step-heading" css={sectionTitleStyle}>
				{intl.formatMessage({
					id: 'ubp.calculator.usage.interestQuestion',
					defaultMessage: 'What are you interested in?',
				})}
			</h3>
			<div css={[cardGridStyle]} role="radiogroup" aria-labelledby="usage-step-heading">
				{usageMeters.map((choice: Meter) => {
					const { label, description, list, info } = USAGE_STRINGS[choice];
					return (
						<ChoiceCard
							key={`usage-${choice}`}
							value={choice}
							selected={meter === choice}
							onSelect={() => setMeter(choice)}
							groupName="group-usage"
							label={label}
							description={description}
							list={list}
							info={info}
						/>
					);
				})}
			</div>
			<div css={orgSizeQuestionStyle}>
				<h3 css={orgSizeHeadingStyle} id="orgUserCount-label">
					<label htmlFor="orgUserCount">
						{intl.formatMessage({
							id: 'ubp.calculator.usage.orgSizeQuestion',
							defaultMessage: 'How many people are in your organization?',
						})}
					</label>
				</h3>
				<div css={inputAndRequiredStyle}>
					<input
						id="orgUserCount"
						type="number"
						css={numberInputStyle}
						value={orgUserCountInput}
						min={0}
						aria-required="true"
						aria-labelledby="orgUserCount-label"
						onChange={(e) => {
							const next = e.target.value;
							setOrgUserCountInput(next);
							setOrgUserCount(next === '' ? undefined : Number(next));
						}}
					/>
					<span css={requiredLabelStyle}>
						{intl.formatMessage({
							id: 'ubp.calculator.usage.required',
							defaultMessage: 'Required',
						})}
					</span>
				</div>
			</div>
			{orgUserCountError && <div css={errorMessageStyle}>{orgUserCountError}</div>}
			<StepNav showNext nextDisabled={!canAdvance} />
		</TypographyStyles>
	);
};

// H1 title styling — extra size overrides for the Figma "D - H1 - Small" (48/56).
// TypographyStyles provides the base font-family/weight; centering + explicit
// 40px vertical padding come from Figma spec (40 above and below the H1).
const pageTitleStyle = css({
	margin: 0,
	textAlign: 'center',
	paddingBlock: '40px',
});

// First H3 — sits above the meter/choice cards. 40px padding below per Figma;
// the H1 above supplies its own 40px bottom padding so no top padding is needed.
const sectionTitleStyle = css({
	margin: 0,
	paddingBlockEnd: '40px',
});

// Second H3 — the "How many people..." question that sits on the SAME row as
// the org-size input. The 60px top padding lives on the parent row wrapper
// (orgSizeQuestionStyle) so the H3 and input stay vertically centered on the
// same baseline; no vertical padding is needed here.
const orgSizeHeadingStyle = css({
	margin: 0,
});

const cardGridStyle = css({
	display: 'grid',
	gap: token('space.400'),
	gridTemplateColumns: '1fr 1fr',
	[media.below.sm]: {
		gridTemplateColumns: '1fr',
	},
});

// Wraps the org-size H3 and the input+Required cluster so they sit on the same
// row — H3 immediately followed by the input + "Required" indicator. Uses a
// natural flow gap (no space-between) so the H3 and input stay adjacent, per
// Figma. 60px top padding lives on the ROW wrapper (not the H3) so both sides
// of the row shift down together and stay vertically aligned.
const orgSizeQuestionStyle = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: token('space.200'),
	flexWrap: 'wrap',
	paddingBlockStart: '60px',
});

// Places the number input and the "Required" indicator side-by-side, with the
// label vertically centered on the input.
const inputAndRequiredStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'),
});

const numberInputStyle = css({
	width: 160,
	height: 40,
	paddingBlock: token('space.100'),
	paddingInline: token('space.200'),
	fontSize: '1rem',
	lineHeight: '24px',
	border: `1px solid ${wacToken('color.neutral.light600')}`,
	borderRadius: 20,
	backgroundColor: wacToken('color.neutral.light0'),
	color: wacToken('color.neutral.light1100'),
});

const requiredLabelStyle = css({
	fontSize: '0.875rem',
	lineHeight: '20px',
	color: wacToken('color.neutral.light1100'),
});

const errorMessageStyle = css({
	marginBlockStart: 4,
	fontSize: '0.8rem',
	color: wacToken('color.secondary.red'),
	textAlign: 'center',
});
